import React, { useState, useEffect } from 'react';
import { Save, Store, Plus, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const labelStyle = { display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px', color: '#111111' };
const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box' };

const slugify = (s) => (s || 'retailer').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'retailer';

const RetailersManager = () => {
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetch(`${API}/api/retailers`)
      .then((r) => r.json())
      .then((d) => setRetailers(Array.isArray(d.retailers) ? d.retailers : []))
      .catch((e) => console.error('Error fetching retailers:', e))
      .finally(() => setLoading(false));
  }, []);

  const updateField = (id, field, value) => {
    setRetailers((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const updatePrice = (id, idx, field, value) => {
    setRetailers((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const prices = [...(r.prices || [])];
      prices[idx] = { ...prices[idx], [field]: value };
      return { ...r, prices };
    }));
  };

  const addPrice = (id) => {
    setRetailers((prev) => prev.map((r) => (r.id === id ? { ...r, prices: [...(r.prices || []), { label: '', amount: '' }] } : r)));
  };

  const removePrice = (id, idx) => {
    setRetailers((prev) => prev.map((r) => (r.id === id ? { ...r, prices: (r.prices || []).filter((_, i) => i !== idx) } : r)));
  };

  const addCard = () => {
    const newId = `${slugify('new store')}-${Date.now()}`;
    setRetailers((prev) => [...prev, {
      id: newId, title: '', prices: [{ label: '', amount: '' }], buttonText: 'Buy now', url: '', caption: '', available: true
    }]);
  };

  const removeCard = (id) => {
    if (!window.confirm('Delete this retailer card? This cannot be undone once you save.')) return;
    setRetailers((prev) => prev.filter((r) => r.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await fetch(`${API}/api/admin/retailers`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ retailers })
      });
      if (response.ok) {
        setMessage('Retailers updated successfully.');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Failed to update. Please try again.');
      }
    } catch (e) {
      console.error('Error saving retailers:', e);
      setMessage('Failed to update. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}><p>Loading...</p></div>;
  }

  return (
    <div style={{ padding: '40px', maxWidth: '820px' }} data-testid="retailers-manager">
      <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Store size={24} color="#111111" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: '700', margin: 0, color: '#111111' }}>
          Other Retailers
        </h2>
      </div>
      <p style={{ color: '#666666', fontSize: '0.95rem', marginBottom: '32px' }}>
        These cards appear in the "Also available from other retailers" section on the Order page, below the Amazon formats. Add a card for each store, set its price lines, the button label and the store link. Turn "Show on site" off to hide a card without deleting it.
      </p>

      {retailers.map((r) => (
        <div
          key={r.id}
          data-testid={`retailer-editor-${r.id}`}
          style={{ backgroundColor: '#FFFFFF', border: '1px solid #E7E0D4', borderRadius: '10px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: '600', margin: 0, color: '#111111' }}>
              {r.title || 'Untitled store'}
            </h3>
            <button
              onClick={() => removeCard(r.id)}
              data-testid={`retailer-delete-${r.id}`}
              title="Delete card"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#cc0000', padding: '4px' }}
            >
              <Trash2 size={18} />
            </button>
          </div>

          <div style={{ marginBottom: '18px' }}>
            <label style={labelStyle}>Store name</label>
            <input type="text" value={r.title || ''} onChange={(e) => updateField(r.id, 'title', e.target.value)} data-testid={`retailer-title-${r.id}`} style={inputStyle} placeholder="e.g. Bookshop.org" />
          </div>

          <div style={{ marginBottom: '18px' }}>
            <label style={labelStyle}>Prices (one line per format)</label>
            {(r.prices || []).map((p, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <input type="text" value={p.label || ''} onChange={(e) => updatePrice(r.id, i, 'label', e.target.value)} data-testid={`retailer-price-label-${r.id}-${i}`} style={{ ...inputStyle, flex: '1' }} placeholder="Format (e.g. Paperback)" />
                <input type="text" value={p.amount || ''} onChange={(e) => updatePrice(r.id, i, 'amount', e.target.value)} data-testid={`retailer-price-amount-${r.id}-${i}`} style={{ ...inputStyle, flex: '1' }} placeholder="Price (e.g. $24.99 AUD)" />
                <button onClick={() => removePrice(r.id, i)} title="Remove price" style={{ border: 'none', background: 'transparent', color: '#cc0000', cursor: 'pointer', flexShrink: 0 }}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <button onClick={() => addPrice(r.id)} data-testid={`retailer-add-price-${r.id}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', border: '1px dashed #C9A24B', background: 'transparent', color: '#9A7B2E', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
              <Plus size={14} /> Add price line
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px', marginBottom: '18px' }}>
            <div>
              <label style={labelStyle}>Button label</label>
              <input type="text" value={r.buttonText || ''} onChange={(e) => updateField(r.id, 'buttonText', e.target.value)} data-testid={`retailer-buttontext-${r.id}`} style={inputStyle} placeholder="e.g. Paperback or Hardcover" />
            </div>
            <div>
              <label style={labelStyle}>Store link (URL)</label>
              <input type="url" value={r.url || ''} onChange={(e) => updateField(r.id, 'url', e.target.value)} data-testid={`retailer-url-${r.id}`} style={inputStyle} placeholder="https://..." />
            </div>
          </div>

          <div style={{ marginBottom: '18px' }}>
            <label style={labelStyle}>Caption under the button (optional)</label>
            <input type="text" value={r.caption || ''} onChange={(e) => updateField(r.id, 'caption', e.target.value)} data-testid={`retailer-caption-${r.id}`} style={inputStyle} placeholder="Short note under the button" />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: '600', color: '#111111' }}>
            <input
              type="checkbox"
              checked={Boolean(r.available)}
              onChange={(e) => updateField(r.id, 'available', e.target.checked)}
              data-testid={`retailer-available-${r.id}`}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            Show on site
          </label>
        </div>
      ))}

      <button
        onClick={addCard}
        data-testid="add-retailer-button"
        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', border: '2px dashed #C9A24B', background: 'transparent', color: '#9A7B2E', padding: '14px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600', marginBottom: '24px' }}
      >
        <Plus size={18} /> Add retailer card
      </button>

      {message && (
        <div style={{ marginBottom: '20px', padding: '14px', borderRadius: '8px', backgroundColor: message.includes('success') ? '#E8F5E9' : '#FFEBEE', border: `1px solid ${message.includes('success') ? '#4CAF50' : '#F44336'}`, color: message.includes('success') ? '#2E7D32' : '#C62828' }}>
          {message}
        </div>
      )}

      <div>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-retailers-button"
          style={{ padding: '14px 32px', backgroundColor: '#111111', color: '#F4F1E8', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', opacity: saving ? 0.6 : 1 }}
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default RetailersManager;
